import { z } from 'zod';

/**
 * Shapes stored locally in IndexedDB and mirrored to Supabase. Everything that
 * crosses a boundary (storage, network) is validated with these schemas, so a
 * stale or corrupt row fails loudly at the edge instead of deep in the UI.
 */

export const instrumentIdSchema = z.enum(['ukulele', 'guitar', 'ocarina', 'piano', 'voice']);
export type InstrumentId = z.infer<typeof instrumentIdSchema>;

export const characterIdSchema = z.enum([
  'tempo',
  'clara',
  'fifi',
  'otto',
  'luna',
  'sol',
  'volt',
  'bram',
]);
export type CharacterId = z.infer<typeof characterIdSchema>;

/**
 * Answers from onboarding that are not settings. The birth year itself is
 * deliberately not stored: the age gate only needs to know the outcome, and
 * keeping a child's date of birth would be data we have no use for.
 */
export const profileFlagsSchema = z
  .object({
    minor: z.boolean().default(false),
    theoryOnly: z.boolean().default(false),
    experience: z.enum(['new', 'some', 'lots']).nullable().default(null),
    micOptIn: z.boolean().default(false),
    soundsOptIn: z.boolean().default(false),
    /** Highest section the placement test cleared; null when it was not taken. */
    placedSection: z.number().int().nullable().default(null),
  })
  // `prefault`, not `default`: the empty object is run through the field
  // defaults above rather than having to spell every one of them out again.
  .prefault({});
export type ProfileFlags = z.infer<typeof profileFlagsSchema>;

export const profileSchema = z.object({
  id: z.string(),
  username: z.string().nullable().default(null),
  displayName: z.string().nullable().default(null),
  isAnonymous: z.boolean().default(true),
  companionCharacter: characterIdSchema.default('tempo'),
  primaryInstrument: instrumentIdSchema.default('ukulele'),
  instruments: z.array(instrumentIdSchema).default(['ukulele']),
  timezone: z.string().default('UTC'),
  dailyGoalXp: z.union([z.literal(10), z.literal(20), z.literal(30), z.literal(50)]).default(20),
  level: z.number().int().min(1).default(1),
  totalXp: z.number().int().min(0).default(0),
  /** Picks. The column keeps the name it was born with; the product word is Picks. */
  gems: z.number().int().min(0).default(0),
  streakCurrent: z.number().int().min(0).default(0),
  streakLongest: z.number().int().min(0).default(0),
  streakLastDate: z.string().nullable().default(null),
  /** The last missed day forgiven as a rest day, as YYYY-MM-DD. */
  streakRestUsedOn: z.string().nullable().default(null),
  isPremium: z.boolean().default(false),
  flags: profileFlagsSchema,
  contentVersion: z.number().int().default(1),
  reminderHour: z.number().int().min(0).max(23).nullable().default(null),
  updatedAt: z.string().default(() => new Date().toISOString()),
  /** True when this profile exists only on this device (no backend configured). */
  localOnly: z.boolean().default(false),
});
export type Profile = z.infer<typeof profileSchema>;

export const skillStateSchema = z.object({
  skillId: z.string(),
  mastery: z.number().min(0).max(1).default(0),
  fsrsState: z.number().int().default(0),
  stability: z.number().default(0),
  difficulty: z.number().default(0),
  dueAt: z.string().nullable().default(null),
  lastReview: z.string().nullable().default(null),
  reps: z.number().int().default(0),
  lapses: z.number().int().default(0),
  streak: z.number().int().default(0),
  // The rest of the FSRS card. Not indexed, so older rows read back with these
  // defaults and no Dexie migration is needed.
  elapsedDays: z.number().default(0),
  scheduledDays: z.number().default(0),
  learningSteps: z.number().int().default(0),
  /** Difficulty band the skill was last asked at, and how often it has been seen. */
  lastDifficulty: z.number().int().default(1),
  seen: z.number().int().default(0),
  /** True once the concept card has been shown. */
  introduced: z.boolean().default(false),
  updatedAt: z.string(),
  dirty: z.boolean().default(true),
});
export type SkillState = z.infer<typeof skillStateSchema>;

export const lessonProgressSchema = z.object({
  key: z.string(), // `${lessonId}|${instrument}`
  lessonId: z.string(),
  instrument: z.string().default(''),
  crowns: z.number().int().min(0).max(5).default(0),
  legendary: z.boolean().default(false),
  timesCompleted: z.number().int().default(0),
  bestAccuracy: z.number().nullable().default(null),
  lastCompletedAt: z.string().nullable().default(null),
  updatedAt: z.string(),
  dirty: z.boolean().default(true),
});
export type LessonProgress = z.infer<typeof lessonProgressSchema>;

export const attemptSchema = z.object({
  id: z.number().int().optional(),
  sessionId: z.string(),
  templateId: z.string(),
  skillIds: z.array(z.string()),
  seed: z.number(),
  difficulty: z.number().int(),
  paramsHash: z.string(),
  correct: z.boolean(),
  score: z.number().min(0).max(1),
  responseMs: z.number().int(),
  usedFallback: z.boolean().default(false),
  answeredAt: z.string(),
});
export type Attempt = z.infer<typeof attemptSchema>;

export const sessionRecordSchema = z.object({
  id: z.string(),
  kind: z.enum(['lesson', 'practice', 'studio', 'song', 'placement', 'checkpoint', 'listening']),
  refId: z.string().nullable().default(null),
  instrument: z.string().nullable().default(null),
  crownLevel: z.number().int().nullable().default(null),
  seed: z.number(),
  startedAt: z.string(),
  endedAt: z.string(),
  exercises: z.number().int(),
  correct: z.number().int(),
  mistakes: z.number().int(),
  maxCombo: z.number().int().default(0),
  accuracy: z.number(),
  xpEarned: z.number().int(),
  gemsEarned: z.number().int().default(0),
  perfect: z.boolean().default(false),
  contentVersion: z.number().int(),
  synced: z.boolean().default(false),
});
export type SessionRecord = z.infer<typeof sessionRecordSchema>;

/**
 * Best result on one arrangement of one song. Mirrors `user_song_scores`; the
 * key is the compound primary key flattened, as lesson progress does.
 */
export const songScoreSchema = z.object({
  key: z.string(), // `${songId}|${instrument}|${arrangement}`
  songId: z.string(),
  instrument: z.string(),
  arrangement: z.enum(['easy', 'medium', 'hard']),
  /** 0–100, the best fraction scaled. */
  bestScore: z.number().int().min(0).max(100).default(0),
  bestStars: z.number().int().min(0).max(3).default(0),
  plays: z.number().int().min(0).default(0),
  /** Three stars at full tempo with hints off, ever. */
  mastered: z.boolean().default(false),
  lastPlayed: z.string().nullable().default(null),
  updatedAt: z.string(),
  dirty: z.boolean().default(true),
});
export type SongScoreRow = z.infer<typeof songScoreSchema>;

/* ----------------------------------------------------------- gamification */

/** One of the day's three quests, as issued and progressed on this device. */
export const questRowSchema = z.object({
  key: z.string(), // `${periodKey}|${questId}`
  periodKey: z.string(),
  questId: z.string(),
  metric: z.string(),
  difficulty: z.enum(['easy', 'medium', 'hard']),
  target: z.number().int().positive(),
  gems: z.number().int().min(0),
  progress: z.number().min(0).default(0),
  completed: z.boolean().default(false),
  claimed: z.boolean().default(false),
  updatedAt: z.string(),
});
export type QuestRow = z.infer<typeof questRowSchema>;

export const achievementRowSchema = z.object({
  achievementId: z.string(),
  progress: z.number().min(0).default(0),
  tier: z.number().int().min(0).default(0),
  claimedTier: z.number().int().min(0).default(0),
  updatedAt: z.string(),
});
export type AchievementRow = z.infer<typeof achievementRowSchema>;

export const chestRowSchema = z.object({
  id: z.string(),
  kind: z.enum(['bronze', 'silver', 'gold']),
  source: z.string(),
  openedAt: z.string().nullable().default(null),
  reward: z.unknown().nullable().default(null),
});
export type ChestRow = z.infer<typeof chestRowSchema>;

export interface CosmeticRow {
  cosmeticId: string;
  source: string;
}

/** A pending write to the backend. Flushed in insertion order by the sync queue. */
export const syncKindSchema = z.enum([
  'session_result',
  'skill_states',
  'lesson_progress',
  'song_score',
  'settings',
  'equip',
  'instrument_settings',
]);
export type SyncKind = z.infer<typeof syncKindSchema>;

export interface SyncItem {
  id?: number;
  kind: SyncKind;
  payload: unknown;
  createdAt: string;
  attempts: number;
  lastError: string | null;
}

export interface KvRow {
  key: string;
  value: unknown;
}

export interface CalibrationRow {
  deviceKey: string;
  latencyMs: number;
  inputGain: number;
  noiseFloor: number;
  updatedAt: string;
}
